import type { TableColumns } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType } from '@pmp/api';

import { interestRateColumns } from '../baseRates/tableConfig';
import { exchangeRateChangeColumns } from '../exchangeRates/tableConfig';
import { advisersCRColumns, assistantsCRColumns } from '../brokers/brokerCRConfig';
import { contactDetailsCRColumns, operationsWhitelistCRColumns, tradingAddressCRColumns } from '../firms/firmCRConfig';

export type SubTableRow = Record<string, unknown>;

/**
 * A collection that travels inside a change request row and that the change request screen shows in a
 * tab of its own: trading addresses, operations, assistants, advisers. The log names its fields with
 * that screen's columns, so a column added there is named here too.
 */
export type SubTable = {
  /** The column that names an item of the collection, shown on every row of it in the detail. */
  rowLabel: string;
  /** i18n key for the collection, which is what the change request screen's tab calls it. */
  title: string;
  columns: TableColumns<SubTableRow>;
  /** Fields the collection's rows carry that say nothing about the item, left out of the log. */
  hiddenFields?: string[];
};

const INTEREST_RATE_HIDDEN_FIELDS = ['baseRateCode', 'index'];
const EXCHANGE_RATE_HIDDEN_FIELDS = ['sourceCurrency', 'targetCurrency'];

/** Keyed by the field the collection travels in, which is the field the log reports it under. */
export const SUB_TABLES: Partial<Record<ActionEntityType, Record<string, SubTable>>> = {
  [ActionEntityType.BASE_RATES]: {
    interestRates: {
      rowLabel: 'startDate',
      title: 'pmp.baseRates.labels.interestRates',
      columns: interestRateColumns.filter(
        column => !INTEREST_RATE_HIDDEN_FIELDS.includes(String(column.key)),
      ) as unknown as TableColumns<SubTableRow>,
      hiddenFields: INTEREST_RATE_HIDDEN_FIELDS,
    },
  },
  [ActionEntityType.EXCHANGE_RATES]: {
    rates: {
      rowLabel: 'startDate',
      title: 'pmp.exchangeRates.labels.exchangeRates',
      columns: exchangeRateChangeColumns.filter(
        column => !EXCHANGE_RATE_HIDDEN_FIELDS.includes(String(column.key)),
      ) as unknown as TableColumns<SubTableRow>,
      hiddenFields: EXCHANGE_RATE_HIDDEN_FIELDS,
    },
  },
  [ActionEntityType.FIRMS]: {
    tradingAddresses: {
      rowLabel: 'name',
      title: 'firms.labels.tradingNames',
      columns: tradingAddressCRColumns as unknown as TableColumns<SubTableRow>,
    },
    operationsWhitelist: {
      rowLabel: 'type',
      title: 'firms.labels.operationsWhitelist',
      columns: operationsWhitelistCRColumns as unknown as TableColumns<SubTableRow>,
    },
  },
  [ActionEntityType.BROKERS]: {
    assistants: {
      rowLabel: 'displayName',
      title: 'lender.brokers.labels.assistants',
      columns: assistantsCRColumns as unknown as TableColumns<SubTableRow>,
    },
    advisers: {
      rowLabel: 'displayName',
      title: 'lender.brokers.labels.advisers',
      columns: advisersCRColumns as unknown as TableColumns<SubTableRow>,
    },
  },
};

/** What the record's own fields are called, so the sections above the collections have a heading too. */
export const RECORD_SECTION_TITLES: Partial<Record<ActionEntityType, string>> = {
  [ActionEntityType.FIRMS]: 'firms.labels.firmTitle',
  [ActionEntityType.BROKERS]: 'lender.brokers.labels.intermediary',
};

/**
 * The record's own fields that the change request screen names in a tab of its own rather than in its
 * main grid, so the log names them too instead of falling back to the model's field name.
 */
export const RECORD_EXTRA_COLUMNS: Partial<Record<ActionEntityType, TableColumns<SubTableRow>>> = {
  [ActionEntityType.FIRMS]: contactDetailsCRColumns as unknown as TableColumns<SubTableRow>,
};

/**
 * Change requests whose rows share the record's id, by the record's type and then by the change
 * request's type, with the collection their rows show under, one item per row key. The backend names
 * the row a change belongs to by its start date. An exchange rate's log is its currency's periods; an
 * interest rate has change requests of its own but lives inside its base rate, so the base rate's log
 * lists them too, as its interest rates.
 */
const ROW_COLLECTIONS: Partial<Record<ActionEntityType, Partial<Record<ActionEntityType, string>>>> = {
  [ActionEntityType.BASE_RATES]: { [ActionEntityType.INTEREST_RATES]: 'interestRates' },
  [ActionEntityType.EXCHANGE_RATES]: { [ActionEntityType.EXCHANGE_RATES]: 'rates' },
};

/** Every entity type whose change requests the record's log lists: its own and the ones filed under its id. */
export const getLoggedEntityTypes = (entityType: ActionEntityType): ActionEntityType[] => [
  ...new Set([entityType, ...(Object.keys(ROW_COLLECTIONS[entityType] ?? {}) as ActionEntityType[])]),
];

/** The collection a change request's rows show under in the record's log, none when they are the record itself. */
export const getRowCollection = (
  entityType: ActionEntityType | null | undefined,
  changeRequestEntityType: string | null | undefined,
): string | undefined =>
  entityType && changeRequestEntityType ? ROW_COLLECTIONS[entityType]?.[changeRequestEntityType as ActionEntityType] : undefined;

export const getSubTables = (entityType: ActionEntityType | null | undefined): Record<string, SubTable> =>
  (entityType && SUB_TABLES[entityType]) || {};

/**
 * The value lists the collections' columns render with. Without them a cell shows the stored code,
 * `ADVICE` where the change request screen says `Advice`.
 */
export const SUB_TABLE_LIST_NAMES: FieldType[] = [
  ...new Set(
    Object.values(SUB_TABLES)
      .flatMap(subTables => Object.values(subTables))
      .flatMap(subTable => subTable.columns.map(column => column.options?.listName))
      .filter((listName): listName is FieldType => !!listName),
  ),
];
